import React, { useEffect, useState } from 'react';
import { Box, Toolbar, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import Navigation from './Navigation';
import authService from '../../services/authService';
import { syncPrivilegesFromBackend } from '../../services/privilegeService';
import { useNavigate } from 'react-router-dom';
import notificationService from '../../services/notificationService';
import API_CONFIG from '../../config';

/**
 * Main layout component for the application
 * Includes navigation and footer with main content area
 * 
 * @param {Object} props Component props
 * @param {JSX.Element} props.children Child components to render in the main content area
 * @returns {JSX.Element} Layout component
 */
const Layout = ({ children }) => {
  const drawerWidth = 0;
  const navigate = useNavigate();
  const [takeoverDialogOpen, setTakeoverDialogOpen] = useState(false);
  const [takeoverRequest, setTakeoverRequest] = useState(null);

  // CRITICAL: Always sync privileges from backend on app load
  // This ensures the API call is always made and visible in network tab
  // This is necessary to verify the system is working based on backend response
  useEffect(() => {
    const syncPrivileges = async () => {
      if (authService.isAuthenticated()) {
        const userRole = authService.getUserRole();
        if (userRole && userRole !== 'ADMIN') {
          console.log(`[Layout] 🔄 Syncing privileges from backend for role: ${userRole}`);
          try {
            // Force refresh to always make API call, even if privileges exist in localStorage
            await syncPrivilegesFromBackend(userRole, true);
            console.log(`[Layout] ✅ Privileges synced successfully from backend`);
          } catch (error) {
            console.error(`[Layout] ❌ Failed to sync privileges:`, error);
          }
        }
      }
    };

    syncPrivileges();
  }, []); // Run only once on mount

  useEffect(() => {
    if (!authService.isAuthenticated()) return;
    if (authService.isSessionExpired()) {
      authService.logout(navigate);
      return;
    }
    authService.updateLastActivity();
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    const handlers = events.map((event) => {
      const handler = () => {
        if (authService.isAuthenticated()) {
          authService.updateLastActivity();
        }
      };
      window.addEventListener(event, handler, true);
      return { event, handler };
    });
    const check = () => {
      if (authService.isSessionExpired()) {
        authService.logout(navigate);
      }
    };
    const intervalId = setInterval(check, 5 * 60 * 1000);
    return () => {
      clearInterval(intervalId);
      handlers.forEach(({ event, handler }) => {
        window.removeEventListener(event, handler, true);
      });
    };
  }, [navigate]);

  // Heartbeat ping to keep session alive on backend
  useEffect(() => {
    if (!authService.isAuthenticated()) return;

    const sendHeartbeat = async () => {
      try {
        const token = authService.getToken();
        if (!token) return;

        const response = await fetch(`${API_CONFIG.BASE_URL}/session/ping`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            console.log('[Layout] Session expired on backend, logging out...');
            authService.logout(navigate);
          }
        } else {
          const data = await response.json();
          console.log('[Layout] Heartbeat successful, lastActivity:', data.lastActivity);
        }
      } catch (error) {
        console.error('[Layout] Heartbeat ping failed:', error);
      }
    };

    // Send heartbeat every 2 minutes (backend timeout is 5 minutes)
    const heartbeatInterval = setInterval(sendHeartbeat, 2 * 60 * 1000);

    // Send initial heartbeat
    sendHeartbeat();

    return () => {
      clearInterval(heartbeatInterval);
    };
  }, [navigate]);

  useEffect(() => {
    if (!authService.isAuthenticated()) return;
    let active = true;
    const poll = async () => {
      try {
        const res = await notificationService.getUnreadNotifications();
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        const myDevice = authService.getDeviceId();
        const req = list.find(n => n.type === 'LOGIN_TAKEOVER_REQUEST' && n.data && n.data.deviceId && n.data.deviceId !== myDevice);
        if (req && active) {
          setTakeoverRequest(req);
          setTakeoverDialogOpen(true);
        }
        const deny = list.find(n => n.type === 'LOGIN_TAKEOVER_DENIED' && n.data && n.data.targetDeviceId === myDevice);
        if (deny) {
          authService.logout(navigate);
        }
      } catch { }
    };
    const interval = setInterval(poll, 5000);
    poll();
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [navigate]);

  const handleAllowTakeover = async () => {
    setTakeoverDialogOpen(false);
    try {
      await notificationService.createNotification({
        type: 'LOGIN_TAKEOVER_ALLOWED',
        title: 'Login allowed',
        message: 'Switching to new device',
        read: false,
        data: {
          targetDeviceId: takeoverRequest?.data?.deviceId
        }
      });
    } catch { }
    authService.logout(navigate);
  };

  const handleDenyTakeover = async () => {
    setTakeoverDialogOpen(false);
    try {
      await notificationService.createNotification({
        type: 'LOGIN_TAKEOVER_DENIED',
        title: 'Login denied',
        message: 'Current device denied login',
        read: false,
        data: {
          targetDeviceId: takeoverRequest?.data?.deviceId
        }
      });
    } catch { }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Navigation />

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          ml: 0, // Remove left margin
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
          position: 'relative',
          transition: 'all 0.2s ease-in-out'
        }}
      >
        <Toolbar /> {/* Spacer to push content below app bar */}
        <Box sx={{
          flexGrow: 1,
          p: 0, // Remove padding
          '& > *': { // Apply styles to direct children
            maxWidth: '100%',
            width: '100%'
          }
        }}>
          {children}
        </Box>
        <Dialog open={takeoverDialogOpen} onClose={() => setTakeoverDialogOpen(false)}>
          <DialogTitle>Another login detected</DialogTitle>
          <DialogContent>
            <Typography>
              Someone is trying to login to this account from another device. Allow them?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDenyTakeover} color="secondary">Not Allow</Button>
            <Button onClick={handleAllowTakeover} color="primary" variant="contained">Allow</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Layout; 
